import React, {
  useState,
  useCallback,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { DateRangeSelect } from '../components/DateRangeSelect';
import { AggregateBy, DateRange } from '../types/types';

interface EntityOverTimeContextType {
  isParentGroup: boolean;
  selectedEntityIds: string[];
  aggregateBy: AggregateBy;
  dateRange?: {
    start: string;
    end: string;
  };
}

const EntityOverTimeContext = createContext<
  EntityOverTimeContextType | undefined
>(undefined);

export const useEntityOverTimeContext = () => {
  const context = useContext(EntityOverTimeContext);
  if (context === undefined) {
    throw new Error(
      'useEntityOverTimeContext must be used within EntityOverTimeProvider',
    );
  }
  return context;
};

export const useOptionalEntityOverTimeContext = () => {
  return useContext(EntityOverTimeContext);
};

export const EntityOverTimeProvider = ({
  children,
  showDailyAggregation = false,
  syncToUrl = false,
}: {
  children: React.ReactNode;
  showDailyAggregation?: boolean;
  syncToUrl?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const startParam = searchParams.get('start');
  const endParam = searchParams.get('end');
  const aggregateByParam = searchParams.get('aggregateBy');

  // Memoised on the primitive param values so its identity stays stable across
  // renders when nothing changed (prevents a write/read feedback loop).
  const urlRange = useMemo<DateRange | null>(() => {
    if (!syncToUrl) return null;
    if (startParam && endParam && aggregateByParam) {
      return {
        start: startParam,
        end: endParam,
        aggregateBy: aggregateByParam as AggregateBy,
      };
    }
    return null;
  }, [syncToUrl, startParam, endParam, aggregateByParam]);

  const [localRange, setLocalRange] = useState<DateRange | null>(null);

  const selectedDateRange = syncToUrl ? urlRange : localRange;

  const handleDateRangeChange = useCallback(
    (dateRange: DateRange) => {
      if (syncToUrl) {
        setSearchParams(
          prev => {
            const params = new URLSearchParams(prev);
            params.set('start', dateRange.start);
            params.set('end', dateRange.end);
            params.set('aggregateBy', dateRange.aggregateBy);
            return params;
          },
          { replace: true },
        );
      } else {
        setLocalRange(dateRange);
      }
    },
    [syncToUrl, setSearchParams],
  );

  const contextValue = useMemo(
    () => ({
      isParentGroup: false,
      selectedEntityIds: [],
      aggregateBy: (selectedDateRange?.aggregateBy ?? 'month') as AggregateBy,
      dateRange: selectedDateRange
        ? {
            start: selectedDateRange.start,
            end: selectedDateRange.end,
          }
        : undefined,
    }),
    [selectedDateRange],
  );

  return (
    <EntityOverTimeContext.Provider value={contextValue}>
      <div>
        <div
          style={{
            marginBottom: 15,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <DateRangeSelect
            onDateRangeChange={handleDateRangeChange}
            defaultTimePeriod="month"
            showDailyAggregation={showDailyAggregation}
            initialRange={syncToUrl ? urlRange ?? undefined : undefined}
          />
        </div>
        {children}
      </div>
    </EntityOverTimeContext.Provider>
  );
};
